import {useState, useCallback} from 'react';
import tinycolor from 'tinycolor2';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import CustomInput from './CustomInput';

const getIsSelected = ({value, multi}) => optionValue => {
  if (multi) return Array.isArray(value) && value.includes(optionValue);
  return value === optionValue;
};

const handleClick = ({
  value, 
  multi, 
  disabled, 
  isControlled, 
  onChange, 
  setInternalValue
}) => optionValue => {
    if (disabled) return;
    let newValue;
    if (multi) {
      const current = Array.isArray(value) ? value : [];
      newValue = current.includes(optionValue)
        ? current.filter(v => v !== optionValue)
        : [...current, optionValue];
    } else {
      newValue = value === optionValue ? null : optionValue;
    }
    if (!isControlled) setInternalValue(newValue);
    if (onChange) onChange({target: {value: newValue}}, newValue);
  };

const baseCardStyle = ({
  color, 
  useColoredBackground, 
  minCardHeight,
  isSelected,
}) => ({
  border: (
    useColoredBackground ? `1px solid ${color}` 
    : isSelected ? `2px solid ${color}`
    : '2px solid rgb(240,240,240)'
  ),
  borderRadius: '8px',
  // boxShadow: useColoredBackground ? `0 2px 4px rgba(0,0,0,0.1)` : '0 2px 4px rgba(0,0,0,0.1)',
  padding: '2em',
  minHeight: minCardHeight || 'none',
  cursor: 'pointer',
  backgroundColor: (
    useColoredBackground
    ? tinycolor(color).setAlpha(0.1).toString() 
    : '#ffffff'
  ),
});

const CardChoice = ({
  value,
  isSelected,
  label,
  description,
  color,
  Icon = null,
  emoji = null,
  align = 'center',
  layout = 'column',
  useColoredBackground = false,
  onClick
}) => {
  const styles = baseCardStyle({color, useColoredBackground, isSelected});
  const isRow = layout === 'row';
  return (
    <Box 
      key={value}
      style={styles} 
      sx={{
        '&:hover': {
          border: `2px solid ${color}`, 
          boxShadow: `0 0 4px ${color}`}
      }}
      onClick={onClick}
      tabIndex={0}
    >
      <Stack
        direction={isRow ? 'row' : 'column'}
        alignItems={isRow ? 'flex-start' : align}
        spacing={2}
      >
        {!!Icon && (
          isRow 
          ? <Icon style={{fontSize: 24, color}} />
          : <Avatar
            variant='rounded'
            style={{backgroundColor: color || 'primary'}}
            sx={{width: isRow ? 24 : 56, height: isRow ? 24 : 56}}
          >
            <Icon />
          </Avatar>
        )}
        {
          emoji &&
          <Typography variant={layout === 'row' ? 'h2' : 'h1'}>
            {emoji}
          </Typography>
        }
        <Typography variant='h2'>{label}</Typography>
        {!isRow && <Typography variant='body1'>{description}</Typography>}
      </Stack>
      {isRow && description && <Typography variant='body1' mt={1}>{description}</Typography>}
    </Box>
  );
};

const DefaultChoice = ({
  label,
  value,
  color = 'primary',
  isSelected,
  disabled,
  useColoredBackground = false,
  onClick,
}) => {
  return (
    <Box
      key={value}
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.75,
        borderRadius: config().styles.defaultRadius,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        border: isSelected ? `0.5px solid ${color}` : config().styles.defaultBorder,
        bgcolor: isSelected ? color : '#fff',
        color: isSelected ? '#fff' : 'text.primary',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
        bgcolor: isSelected ? color : '#fff',
        '&:hover': disabled ? {} : {
          bgcolor: (
            isSelected 
            ? color 
            : 'grey.100'
          ),
        },
      }}
    >
      {label}
    </Box>
  );
};

const ChoiceField = props => {
  const {
    options = [],
    value: controlledValue,
    defaultValue,
    onChange,
    multi = false,
    disabled = false,
    color = 'primary',
    choiceStyle = 'default',
    layout = 'column',
    spacing = 2,
    useColoredBackground = false,
    ...rest
  } = props;

  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? (multi ? [] : null)
  );
  const value = isControlled ? controlledValue : internalValue;

  const isSelected = useCallback(getIsSelected({value, multi}), [value, multi]);

  const onClick = useCallback(handleClick({
    value, 
    multi, 
    disabled, 
    isControlled, 
    onChange, 
    setInternalValue
  }), [value, multi, disabled, isControlled, onChange]);

  const Wrapper = (
    choiceStyle === 'card' 
    ? Grid
    : Stack
  );
  const wrapperProps = (
    choiceStyle === 'card'
    ? { container: true, spacing: spacing || 2, width: '100%' }
    : { direction: 'row', spacing: spacing || 2, flexWrap: 'wrap', useFlexGap: true }
  );

  return (
    <CustomInput {...rest}>
      <select
        multiple={multi}
        value={multi ? (Array.isArray(value) ? value : []) : (value ?? '')}
        readOnly
        style={{display: 'none'}}
        onChange={() => {}}
      >
        <option value='' />
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Wrapper {...wrapperProps}>
        {options.map(o => {
          const selected = isSelected(o.value);
          const childProps = {
            label: o.label,
            value: o.value,
            description: o.description,
            Icon: o?.Icon,
            emoji: o?.emoji,
            color: (
              o?.color 
              ? config().colors[o.color] 
              : config().colors[color]
            ),
            isSelected: selected,
            disabled: disabled,
            onClick: () => onClick(o.value),
            useColoredBackground,
            align: o?.align || 'center',
            layout: layout,
          };
          if (choiceStyle === 'card') {
            return (
              <Grid item xs={o?.xs || 12} md={o?.md || 6} lg={o?.lg || 4} xl={o?.xl || 3} alignItems='stretch'>
                <CardChoice {...childProps} />
              </Grid>
            );
          }
          return <DefaultChoice {...childProps} />;
        })}
      </Wrapper>
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
      </Stack>
    </CustomInput>
  );
};

export default ChoiceField;
